import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  CircularProgress,
  Alert,
  useTheme,
  ThemeProvider,
  createTheme,
  StepContent,
  IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ServiceSelection from './ServiceSelection';
import PractitionerSelection from './PractitionerSelection';
import TimeSelection from './TimeSelection';
import PatientForm from './PatientForm';
import PaymentForm from './PaymentForm';
import Confirmation from './Confirmation';
import { getClinicInfo, getClinicServices, getAvailableTimes, createBooking, getPractitioners } from './bookingService';

interface BookingPageProps {
  clinicId?: string;
  clinicPath?: string;
  serviceId?: string;
  returnUrl?: string;
}

interface Clinic {
  id: number;
  name: string;
  primary_color: string;
  logo_url?: string;
  business_id: string;
  practitioner_id: string; // Default practitioner ID
}

interface Practitioner {
  id: string;
  name: string;
  title?: string;
  specialties?: string;
  description?: string;
  photo_url?: string;
}

interface Service {
  id: string;
  name: string;
  price: number | null;
  description: string | null;
  duration_in_minutes: number;
}

interface AvailableTime {
  iso: string;
  date: string;
  time: string;
  day_of_week: string;
  formatted: string;
}

interface BookingData {
  service: Service | null;
  practitioner: Practitioner | null;
  appointmentTime: AvailableTime | null;
  patient: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  notes: string;
  payment?: {
    status: 'pending' | 'processing' | 'complete' | 'failed';
    transactionId?: string;
    accessCode?: string;
  };
}

const steps = ['Select Service', 'Select Practitioner', 'Choose Date & Time', 'Your Details', 'Payment', 'Confirmation'];

const BookingPage: React.FC<BookingPageProps> = ({ clinicId, clinicPath, serviceId, returnUrl }) => {
  const defaultTheme = useTheme();
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [availableTimes, setAvailableTimes] = useState<AvailableTime[]>([]);
  const [customTheme, setCustomTheme] = useState(defaultTheme);
  
  // Booking data
  const [bookingData, setBookingData] = useState<BookingData>({
    service: null,
    practitioner: null,
    appointmentTime: null,
    patient: {
      first_name: '',
      last_name: '',
      email: '',
      phone: ''
    },
    notes: '',
    payment: {
      status: 'pending'
    }
  });
  
  // Check if we're in an iframe
  const [isInIframe, setIsInIframe] = useState<boolean>(false);
  
  // Check if we're on a payment completion page
  const [isPaymentComplete, setIsPaymentComplete] = useState<boolean>(false);
  
  // Setup and debugging
  useEffect(() => {
    console.log('BOOKING PAGE LOADED:');
    console.log('- Current URL:', window.location.href);
    console.log('- Document referrer:', document.referrer);
    console.log('- Service ID:', serviceId);
    console.log('- Return URL:', returnUrl);
    
    // Check if we're in an iframe
    try {
      setIsInIframe(window.self !== window.top);
      console.log('- Is in iframe:', window.self !== window.top);
    } catch (e) {
      // If we can't access window.top due to cross-origin restrictions,
      // we're definitely in an iframe
      setIsInIframe(true);
      console.log('- Is in iframe: true (detected from security exception)');
    }
    
    // Check if we're on a payment completion page
    const isComplete = window.location.href.includes('payment-complete') || 
                       window.location.href.includes('AccessCode=');
    setIsPaymentComplete(isComplete);
    console.log('- Is payment complete page:', isComplete);
    
    // Debug the current URL more thoroughly
    if (isComplete) {
      console.log('PAYMENT COMPLETE URL ANALYSIS:');
      console.log('- Full URL:', window.location.href);
      console.log('- Has returnUrl parameter:', !!returnUrl);
      console.log('- Current URL parameters:', window.location.search);
      
      // If we don't have returnUrl in props but it's in the URL, try to extract it
      if (!returnUrl && window.location.search) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlReturnUrl = urlParams.get('returnUrl');
        if (urlReturnUrl) {
          console.log('- Found returnUrl in URL parameters:', urlReturnUrl);
          try {
            const decodedReturnUrl = decodeURIComponent(urlReturnUrl);
            console.log('- Decoded URL returnUrl:', decodedReturnUrl);
            // We can't update the returnUrl prop, but we'll use this in local storage so the button can use it
            localStorage.setItem('bookingReturnUrl', decodedReturnUrl);
          } catch (e) {
            console.error('- Error decoding URL returnUrl:', e);
          }
        }
      }
      
      // If we're on a payment completion page, automatically show the success step
      setActiveStep(6); // Success step
      console.log('- Setting to success step automatically');
    }
    
    // Try to decode the returnUrl if it exists
    if (returnUrl) {
      try {
        const decodedUrl = decodeURIComponent(returnUrl);
        console.log('- Decoded return URL:', decodedUrl);
      } catch (e) {
        console.error('- Error decoding returnUrl:', e);
      }
    }
  }, [serviceId, returnUrl]);
  
  // Load clinic data on component mount
  useEffect(() => {
    const fetchClinic = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get clinic info
        const clinicResult = await getClinicInfo(clinicId, clinicPath);
        
        if (!clinicResult || !clinicResult.clinic) {
          throw new Error('Clinic not found');
        }
        
        setClinic(clinicResult.clinic);
        
        // Create custom theme with clinic's primary color
        if (clinicResult.clinic.primary_color) {
          setCustomTheme(createTheme({
            ...defaultTheme,
            palette: {
              ...defaultTheme.palette,
              primary: {
                main: clinicResult.clinic.primary_color,
              },
            },
          }));
        }
        
        // Get services for this clinic
        const servicesResult = await getClinicServices(clinicResult.clinic.id);
        setServices(servicesResult.appointment_types || []);
        
        // Get practitioners for this clinic
        const practitionersResult = await getPractitioners(clinicResult.clinic.id);
        setPractitioners(practitionersResult.practitioners || []);
        
        // If serviceId is provided, pre-select that service
        if (serviceId && servicesResult.appointment_types) {
          const selectedService = servicesResult.appointment_types.find(
            service => service.id === serviceId
          );
          
          if (selectedService) {
            console.log('Pre-selecting service:', selectedService.name);
            setBookingData(prev => ({
              ...prev,
              service: selectedService
            }));
            
            // If we're auto-selecting a service, advance to the next step
            if (!isPaymentComplete) {
              setActiveStep(1); // Go to practitioner selection
            }
          }
        }
        
      } catch (err: any) {
        console.error('Error loading clinic data:', err);
        setError(err.message || 'Failed to load clinic data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchClinic();
  }, [clinicId, clinicPath, serviceId, isPaymentComplete]);
  
  // Load available times when practitioner and service are selected
  useEffect(() => {
    const fetchAvailableTimes = async () => {
      if (!bookingData.service || !bookingData.practitioner || !clinic) return;
      
      try {
        setLoading(true);
        
        // Get the current date
        const today = new Date();
        const fromDate = today.toISOString().split('T')[0];
        
        // Get the date 2 weeks from now
        const twoWeeksLater = new Date(today);
        twoWeeksLater.setDate(today.getDate() + 14);
        const toDate = twoWeeksLater.toISOString().split('T')[0];
        
        // If we want to show more availability (up to 2 months)
        // const endDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        // const toDate = endDate.toISOString().split('T')[0];
        
        console.log(`Fetching available times from ${fromDate} to ${toDate}`);
        
        const timesResult = await getAvailableTimes({
          clinic_id: clinic.id.toString(),
          appointment_type_id: bookingData.service.id,
          practitioner_id: bookingData.practitioner.id,
          from_date: fromDate,
          to_date: toDate
        });
        
        setAvailableTimes(timesResult.available_times || []);
      } catch (err: any) {
        console.error('Error loading available times:', err);
        setError(err.message || 'Failed to load available times');
      } finally {
        setLoading(false);
      }
    };
    
    if (bookingData.service && bookingData.practitioner) {
      fetchAvailableTimes();
    }
  }, [bookingData.service, bookingData.practitioner, clinic]);
  
  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };
  
  const handleServiceSelect = (service: Service) => {
    setBookingData({
      ...bookingData,
      service,
      practitioner: null, // Reset practitioner when service changes
      appointmentTime: null // Reset time selection when service changes
    });
    handleNext();
  };
  
  const handlePractitionerSelect = (practitioner: Practitioner) => {
    setBookingData({
      ...bookingData,
      practitioner,
      appointmentTime: null // Reset time selection when practitioner changes
    });
    handleNext();
  };
  
  const handleTimeSelect = (time: AvailableTime) => {
    setBookingData({
      ...bookingData,
      appointmentTime: time
    });
    handleNext();
  };
  
  const handlePatientInfoSubmit = (patientData: BookingData['patient'], notes: string) => {
    setBookingData({
      ...bookingData,
      patient: patientData,
      notes
    });
    handleNext();
  };
  
  const handlePaymentSuccess = (paymentData: { 
    status: 'complete', 
    transactionId: string,
    accessCode: string 
  }) => {
    setBookingData({
      ...bookingData,
      payment: {
        status: 'complete',
        transactionId: paymentData.transactionId,
        accessCode: paymentData.accessCode
      }
    });
    handleNext();
  };
  
  const handlePaymentCancel = () => {
    handleBack();
  };
  
  const handleConfirmBooking = async () => {
    if (!clinic || !bookingData.service || !bookingData.practitioner || !bookingData.appointmentTime) {
      setError('Booking information is incomplete');
      return;
    }
    
    // Check if payment is complete
    if (!bookingData.payment || bookingData.payment.status !== 'complete') {
      setError('Payment must be completed before confirming booking');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Use the ISO string directly from bookingData, this contains the correct UTC time expected by Cliniko
      // This preserves the UTC time that Cliniko sent, which is what they expect to receive back
      const startTimeIso = bookingData.appointmentTime.iso;
      
      // Log timezone debugging information
      console.log('Booking timezone debugging:');
      console.log('  Selected time ISO:', startTimeIso);
      console.log('  Selected time local:', new Date(startTimeIso).toString());
      console.log('  Selected time formatted:', bookingData.appointmentTime.formatted);
      
      // Calculate end time based on service duration - we need to calculate this since we only have the start time
      const startTime = new Date(startTimeIso);
      const endTime = new Date(startTime.getTime() + (bookingData.service.duration_in_minutes * 60000));
      
      // Include payment information in notes
      const paymentNotes = `Payment completed: Transaction ID ${bookingData.payment.transactionId}`;
      const combinedNotes = bookingData.notes 
        ? `${bookingData.notes}\n\n${paymentNotes}` 
        : paymentNotes;
      
      const bookingResult = await createBooking({
        clinic_id: clinic.id.toString(),
        appointment_type_id: bookingData.service.id,
        practitioner_id: bookingData.practitioner.id,
        starts_at: startTime.toISOString(),
        ends_at: endTime.toISOString(),
        patient_first_name: bookingData.patient.first_name,
        patient_last_name: bookingData.patient.last_name,
        patient_email: bookingData.patient.email,
        patient_phone: bookingData.patient.phone,
        notes: combinedNotes
      });
      
      // Success! Booking is confirmed
      setActiveStep(5); // Move to success step (beyond confirmation)
      
    } catch (err: any) {
      console.error('Error creating booking:', err);
      setError(err.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };
  
  // Determine if the next button should be disabled
  const isNextDisabled = () => {
    switch (activeStep) {
      case 0: // Service selection
        return !bookingData.service;
      case 1: // Practitioner selection
        return !bookingData.practitioner;
      case 2: // Time selection
        return !bookingData.appointmentTime;
      case 3: // Patient info
        return !bookingData.patient.first_name || 
               !bookingData.patient.last_name || 
               !bookingData.patient.email;
      case 4: // Payment
        // Payment step doesn't use the next button - it's handled within the payment component
        return true;
      case 5: // Confirmation
        return !bookingData.payment || bookingData.payment.status !== 'complete';
      default:
        return false;
    }
  };
  
  // Render content based on current step
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <ServiceSelection 
            services={services} 
            selectedService={bookingData.service}
            onServiceSelect={handleServiceSelect}
          />
        );
      case 1:
        return (
          <PractitionerSelection 
            practitioners={practitioners} 
            selectedPractitioner={bookingData.practitioner}
            onPractitionerSelect={handlePractitionerSelect}
          />
        );
      case 2:
        return (
          <TimeSelection 
            availableTimes={availableTimes} 
            selectedTime={bookingData.appointmentTime}
            onTimeSelect={handleTimeSelect}
          />
        );
      case 3:
        return (
          <PatientForm 
            patientInfo={bookingData.patient}
            notes={bookingData.notes} 
            onSubmit={handlePatientInfoSubmit}
          />
        );
      case 4:
        if (!clinic || !bookingData.service || !bookingData.practitioner || !bookingData.appointmentTime) {
          return <Alert severity="error">Missing clinic, service, practitioner, or appointment time information</Alert>;
        }
        // Calculate the appointment start and end times
        const startTime = new Date(bookingData.appointmentTime.iso);
        const endTime = new Date(startTime.getTime() + (bookingData.service.duration_in_minutes * 60000));
        
        // Include payment information in notes (if provided)
        const bookingNotes = bookingData.notes || '';
        
        return (
          <PaymentForm 
            clinicId={clinic.id.toString()}
            serviceId={bookingData.service.id}
            practitionerId={bookingData.practitioner.id}
            amount={bookingData.service.price !== null ? Number(bookingData.service.price) : 0}
            patientEmail={bookingData.patient.email}
            patientName={`${bookingData.patient.first_name} ${bookingData.patient.last_name}`}
            // Pass all appointment details for automatic creation
            appointmentDetails={{
              starts_at: startTime.toISOString(),
              ends_at: endTime.toISOString(),
              practitioner_id: bookingData.practitioner.id,
              patient_first_name: bookingData.patient.first_name,
              patient_last_name: bookingData.patient.last_name,
              patient_phone: bookingData.patient.phone,
              notes: bookingNotes
            }}
            returnUrl={returnUrl}
            onSuccess={handlePaymentSuccess}
            onCancel={handlePaymentCancel}
          />
        );
      case 5:
        return (
          <Confirmation 
            bookingData={bookingData}
            clinic={clinic}
            onConfirm={handleConfirmBooking}
          />
        );
      case 6:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" gutterBottom style={{backgroundColor: 'red', padding: '10px', color: 'white'}}>
              🎉 Payment Successful and Appointment Confirmed! 🎉
            </Typography>
            <Typography>
              Your appointment has been booked successfully. You should receive a confirmation email shortly.
            </Typography>
            {bookingData.service && bookingData.practitioner && bookingData.appointmentTime && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1">
                  {bookingData.service.name} with {bookingData.practitioner.name}
                </Typography>
                <Typography>
                  {bookingData.appointmentTime.formatted}
                </Typography>
                {bookingData.payment && bookingData.payment.transactionId && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Payment Transaction ID: {bookingData.payment.transactionId}
                  </Typography>
                )}
              </Box>
            )}
            <Box sx={{ mt: 4 }}>
              <Button 
                variant="contained"
                style={{backgroundColor: 'green', fontSize: '18px', padding: '15px'}}
                color="primary"
                onClick={() => {
                  console.log('Return button clicked');
                  
                  // Check for returnUrl in props or localStorage
                  const storedReturnUrl = localStorage.getItem('bookingReturnUrl');
                  console.log('Return URL (prop):', returnUrl);
                  console.log('Return URL (localStorage):', storedReturnUrl);
                  
                  // First try the prop returnUrl
                  if (returnUrl) {
                    try {
                      const decodedUrl = decodeURIComponent(returnUrl);
                      console.log('Returning to provided prop URL:', decodedUrl);
                      window.location.href = decodedUrl;
                      return;
                    } catch (e) {
                      console.error('Error decoding prop returnUrl:', e);
                    }
                  }
                  
                  // Then try localStorage returnUrl
                  if (storedReturnUrl) {
                    try {
                      console.log('Returning to stored URL:', storedReturnUrl);
                      window.location.href = storedReturnUrl;
                      return;
                    } catch (e) {
                      console.error('Error using stored returnUrl:', e);
                    }
                  }
                  
                  // Try window.close() if no returnUrl
                  console.log('No returnUrl, attempting window.close()');
                  
                  try {
                    window.close();
                    
                    // If window doesn't close, redirect to booking page after a delay
                    setTimeout(() => {
                      if (!document.hidden) {
                        console.log('Window did not close, returning to booking page');
                        window.location.href = '/booking/bettermd';
                      }
                    }, 500);
                  } catch (e) {
                    console.error('Error during close:', e);
                    window.location.href = '/booking/bettermd';
                  }
                }}
              >
                Return to Home
              </Button>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };
  
  // Mobile-optimized stepper for smaller screens
  const renderMobileStepper = () => (
    <Stepper activeStep={activeStep} orientation="vertical">
      {steps.map((label, index) => (
        <Step key={label}>
          <StepLabel>{label}</StepLabel>
          <StepContent>
            {renderStepContent(index)}
            <Box sx={{ mb: 2, mt: 2 }}>
              <div>
                {index !== 3 && (
                  <Button
                    variant="contained"
                    onClick={index === 4 ? handleConfirmBooking : handleNext}
                    sx={{ mt: 1, mr: 1 }}
                    disabled={isNextDisabled() || loading}
                  >
                    {loading && <CircularProgress size={24} sx={{ mr: 1 }} />}
                    {index === 4 ? 'Confirm Booking' : 'Continue'}
                  </Button>
                )}
                {index !== 5 && (
                  <Button
                    disabled={index === 0 || loading}
                    onClick={handleBack}
                    sx={{ mt: 1, mr: 1 }}
                  >
                    Back
                  </Button>
                )}
              </div>
            </Box>
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
  
  // Desktop stepper for larger screens
  const renderDesktopStepper = () => (
    <>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      
      {renderStepContent(activeStep)}
      
      {activeStep < 5 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            Back
          </Button>
          
          {(activeStep < 3 || activeStep === 4) && (
            <Button
              variant="contained"
              onClick={activeStep === 4 ? handleConfirmBooking : handleNext}
              disabled={isNextDisabled() || loading}
            >
              {loading && <CircularProgress size={24} sx={{ mr: 1 }} />}
              {activeStep === 4 ? 'Confirm Booking' : 'Next'}
            </Button>
          )}
        </Box>
      )}
    </>
  );
  
  const handleBackButtonClick = () => {
    // Check if there's a document referrer (previous page)
    if (document.referrer) {
      window.location.href = document.referrer;
    } else {
      // If no referrer, try to go back in history
      window.history.back();
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Back button */}
        <Box sx={{ position: 'absolute', left: 16, top: 16 }}>
          <IconButton 
            aria-label="back"
            onClick={handleBackButtonClick}
            sx={{ 
              bgcolor: 'rgba(0, 0, 0, 0.05)', 
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' } 
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
        
        {loading && activeStep === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Clinic header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              {clinic?.logo_url && (
                <Box sx={{ mb: 2 }}>
                  <img 
                    src={clinic.logo_url} 
                    alt={`${clinic.name} logo`} 
                    style={{ maxHeight: 60, maxWidth: '100%' }} 
                  />
                </Box>
              )}
              <Typography variant="h4" component="h1" gutterBottom>
                {clinic?.name || 'Medical Appointment Booking'}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Book your appointment online
              </Typography>
            </Box>
            
            <Paper
              elevation={3}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2,
              }}
            >
              {/* Show different stepper based on screen size */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                {renderMobileStepper()}
              </Box>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                {renderDesktopStepper()}
              </Box>
            </Paper>
          </>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default BookingPage;